import { useState, type FormEvent } from 'react';
import { User } from 'lucide-react';

import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { Select } from '@/components/ui/Select';
import {
  periodRange,
  periodTitle,
  WEEKDAY_LABEL,
} from '@/features/teacher/components/timetable/timetableCopy';
import { toast } from '@/hooks/useToast';
import { ApiRequestError } from '@/lib/apiClient';
import { capitalizeWords } from '@/lib/format';
import type { AllottableSubject, SectionWeekEntry } from '@/types/timetableAdmin';
import { instituteTimetableApi } from '../api/timetable.api';
import { subjectTheme } from '../subjectTheme';
import { REPEATED_SUBJECT_WARNING, repeatsSubjectOnDay } from '../timetableWeek';

export interface EditLessonModalProps {
  entry: SectionWeekEntry;
  sectionName: string;
  /** Subjects somebody is allotted in this section — what the slot may become. */
  subjects: AllottableSubject[];
  /** The section's lessons this week, to warn when the new subject is already on this day. */
  entries: SectionWeekEntry[];
  onClose: () => void;
  onSaved: () => void;
}

/**
 * What this slot teaches, and who takes it.
 *
 * This dialog used to offer the day and the period instead — which is the one
 * thing dragging the card already does, and none of what somebody opens "edit
 * slot" to fix. Wednesday period 3 should have been Science: that is a subject
 * change, and until now the only way to make it was to delete the lesson and
 * place it again, losing the row and anything recorded against it.
 *
 * **The teacher is shown, not chosen.** Who takes a slot is the allotment of
 * this section and this subject (`TeacherAllocation`) — it is not stored on the
 * lesson, deliberately, so that the timetable and every teacher's own screen
 * cannot come to disagree about who teaches what. Picking a subject here
 * therefore picks the teacher with it, and the panel below says who that is
 * before anything is saved. To put a different person in front of the class,
 * change the allotment; the timetable follows.
 *
 * Moving the lesson is still a drag on the grid, or "Move to another slot" in
 * the card's own menu.
 */
export function EditLessonModal({
  entry,
  sectionName,
  subjects,
  entries,
  onClose,
  onSaved,
}: EditLessonModalProps) {
  const [subjectUuid, setSubjectUuid] = useState(entry.subject.uuid);
  const [roomName, setRoomName] = useState(entry.roomName ?? '');
  const [clashError, setClashError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  /**
   * The subject as it will be once saved. A lesson can outlive its allotment —
   * the subject was dropped, or the teacher who held it left — so the entry's
   * own subject is offered even when the allotment no longer lists it, rather
   * than the form silently switching the slot to something else on open.
   */
  const options = subjects.some((subject) => subject.uuid === entry.subject.uuid)
    ? subjects
    : [{ uuid: entry.subject.uuid, name: entry.subject.name, teacher: entry.teacher ?? '' }, ...subjects];

  const chosen = options.find((subject) => subject.uuid === subjectUuid) ?? null;
  const teacher = chosen?.teacher || null;
  const isReassigning = subjectUuid !== entry.subject.uuid;
  // Only a change of subject can create the repeat; an untouched slot is not news.
  const repeatsSubject =
    isReassigning && repeatsSubjectOnDay(entries, subjectUuid, entry.dayOfWeek, entry.uuid);
  const theme = subjectTheme(chosen?.name ?? entry.subject.name);

  async function save(event: FormEvent) {
    event.preventDefault();
    if (isSaving) return;

    setIsSaving(true);
    setClashError(null);
    try {
      await instituteTimetableApi.updateEntry(entry.uuid, {
        // The cell is sent unchanged: this dialog is about what the lesson is,
        // and the server takes the whole slot rather than a patch of it.
        periodUuid: entry.period.uuid,
        dayOfWeek: entry.dayOfWeek,
        subjectUuid,
        // Always sent from this form, so clearing the box clears the room. A
        // drag leaves it out instead, which keeps whatever it had.
        roomName: roomName.trim(),
      });
      toast.success(
        isReassigning
          ? `This slot is now ${chosen?.name ? capitalizeWords(chosen.name) : 'updated'}`
          : `${capitalizeWords(entry.subject.name)} is updated`,
        `${sectionName} · ${WEEKDAY_LABEL[entry.dayOfWeek]}, ${periodTitle(entry.period)}.`,
      );
      onSaved();
      onClose();
    } catch (caught) {
      const errorMessage =
        caught instanceof ApiRequestError
          ? caught.message
          : 'Something went wrong. Please try again.';

      if (caught instanceof ApiRequestError && caught.message.includes('already takes')) {
        setClashError(caught.message);
      } else {
        toast.error('Could not save this lesson', errorMessage);
      }
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <Modal
      isOpen
      onClose={onClose}
      title="Edit slot"
      description={`${sectionName} · ${WEEKDAY_LABEL[entry.dayOfWeek]}, ${periodTitle(entry.period)} (${periodRange(entry.period.startTime, entry.period.endTime)})`}
      size="sm"
      footer={
        <>
          <Button variant="secondary" size="md" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="md"
            type="submit"
            form="edit-lesson-form"
            isLoading={isSaving}
            className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold"
          >
            {repeatsSubject ? 'Save anyway' : 'Save changes'}
          </Button>
        </>
      }
    >
      <form id="edit-lesson-form" onSubmit={save} className="flex flex-col gap-4 py-1">
        <Select
          label="Subject"
          options={options.map((subject) => ({ value: subject.uuid, label: capitalizeWords(subject.name) }))}
          value={subjectUuid}
          onChange={(event) => {
            setSubjectUuid(event.target.value);
            setClashError(null);
          }}
        />

        {/* Who takes it, decided by the subject above rather than chosen here. */}
        <div className="flex items-start gap-3 rounded-field border border-line bg-surface-2 p-3">
          <span className={`p-2 rounded-lg shrink-0 ${theme.iconBg}`}>
            {teacher ? <User className="h-3.5 w-3.5" /> : theme.icon}
          </span>
          <div className="min-w-0">
            <p className="lbl mb-0.5">Teacher</p>
            <p className="text-sm font-bold text-ink truncate">
              {teacher ?? 'Nobody is allotted this subject here'}
            </p>
            <p className="text-xs text-ink-3 mt-1">
              {teacher
                ? 'Read from the class allotment. Change the allotment to put somebody else in front of this class.'
                : 'Allot this subject in this section first — the timetable takes the teacher from there.'}
            </p>
          </div>
        </div>

        {repeatsSubject && (
          <Alert tone="warning" title="Already on this day">
            {REPEATED_SUBJECT_WARNING} You can still save it if that is intended.
          </Alert>
        )}

        {clashError && (
          <Alert tone="critical" title="Could not save this lesson">
            {clashError}
          </Alert>
        )}

        <Input
          label="Room (optional)"
          placeholder="e.g. Lab 2"
          maxLength={40}
          value={roomName}
          onChange={(event) => setRoomName(event.target.value)}
          hint="Leave it empty to take the room off this lesson."
        />
      </form>
    </Modal>
  );
}
